import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productsService } from '../services/products';
import { useCartStore } from '../store/useCartStore';
import { ProductVariation } from '../types/woocommerce';
import { 
  ShoppingCartIcon, 
  ChevronLeftIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

export const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const addToCart = useCartStore((state) => state.addToCart);
  const setCartOpen = useCartStore((state) => state.setCartOpen);
  
  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  // Fonction pour extraire les meta données importantes
  const getMetaValue = (product: any, key: string): string | null => {
    const meta = product.meta_data?.find((m: any) => m.key === key);
    return meta?.value || null;
  };

  // Récupérer le produit
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => productsService.getProductBySlug(slug!),
    enabled: !!slug,
  });

  // Fonction pour gérer la sélection de variation et changer l'image
  const handleVariationChange = (variation: ProductVariation) => {
    setSelectedVariation(variation);
    
    // Si la variation a une image, trouver son index dans les images du produit
    if (variation.image && variation.image.id && product) {
      const imageIndex = product.images.findIndex(img => img.id === variation.image.id);
      if (imageIndex !== -1) {
        setSelectedImage(imageIndex);
      } else {
        // L'image de la variation n'est pas dans les images du produit
        // On va l'ajouter temporairement et l'afficher
        setSelectedImage(product.images.length);
      }
    }
  };

  // Créer une liste d'images qui inclut l'image de la variation si nécessaire
  const displayImages = useMemo(() => {
    if (!product) return [];
    
    const images = [...product.images];
    
    // Si une variation est sélectionnée et a une image qui n'est pas dans les images du produit
    if (selectedVariation?.image && selectedVariation.image.id) {
      const imageExists = images.some(img => img.id === selectedVariation.image.id);
      if (!imageExists) {
        images.push(selectedVariation.image);
      }
    }
    
    return images;
  }, [product, selectedVariation]);

  // S'assurer que selectedImage reste dans les limites
  useEffect(() => {
    if (displayImages.length > 0 && selectedImage >= displayImages.length) {
      setSelectedImage(displayImages.length - 1);
    }
  }, [displayImages, selectedImage]);

  // Récupérer les variations si c'est un produit variable
  const { data: variationsData } = useQuery({
    queryKey: ['variations', product?.id],
    queryFn: () => productsService.getProductVariations(product!.id),
    enabled: !!product && product.type === 'variable',
  });

  // Récupérer les produits liés
  const { data: relatedProducts } = useQuery({
    queryKey: ['related', product?.id],
    queryFn: () => productsService.getRelatedProducts(product!.id),
    enabled: !!product && product.related_ids.length > 0,
  });

  // Récupérer les produits recommandés (upsell)
  const { data: upsellProducts } = useQuery({
    queryKey: ['upsell', product?.id],
    queryFn: async () => {
      if (!product || !product.upsell_ids.length) return [];
      const promises = product.upsell_ids.slice(0, 4).map((id: number) => 
        productsService.getProduct(id).catch(() => null)
      );
      const results = await Promise.all(promises);
      return results.filter((p): p is NonNullable<typeof p> => p !== null);
    },
    enabled: !!product && product.upsell_ids?.length > 0,
  });

  if (isLoading) {
    return (
      <div className="container-custom py-12">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-200 h-96 rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container-custom py-12">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">
            Erreur lors du chargement du produit
          </p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    if (product.type === 'variable' && !selectedVariation) {
      alert('Veuillez sélectionner une variation');
      return;
    }

    addToCart(product, quantity, selectedVariation || undefined);
    setCartOpen(true);
  };

  const currentPrice = selectedVariation
    ? selectedVariation.price
    : product.price;

  const regularPrice = selectedVariation
    ? selectedVariation.regular_price
    : product.regular_price;

  const salePrice = selectedVariation
    ? selectedVariation.sale_price
    : product.sale_price;

  const isOnSale = selectedVariation
    ? selectedVariation.on_sale
    : product.on_sale;

  const isInStock = selectedVariation
    ? productsService.isInStock(selectedVariation)
    : productsService.isInStock(product);

  const discountPercentage = isOnSale && salePrice
    ? productsService.getDiscountPercentage(regularPrice, salePrice)
    : 0;

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-custom py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 hover:text-primary-600"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Retour
          </button>
          <span>/</span>
          {product.categories.map((cat, index) => (
            <React.Fragment key={cat.id}>
              <Link to={`/category/${cat.slug}`} className="hover:text-primary-600">
                {cat.name}
              </Link>
              {index < product.categories.length - 1 && <span>/</span>}
            </React.Fragment>
          ))}
          <span>/</span>
          <span className="text-gray-900">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="bg-white rounded-lg overflow-hidden mb-4">
              {displayImages.length > 0 ? (
                <img
                  src={displayImages[selectedImage].src}
                  alt={displayImages[selectedImage].alt || product.name}
                  className="w-full h-[500px] object-contain"
                />
              ) : (
                <div className="w-full h-[500px] bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400">Aucune image disponible</span>
                </div>
              )}
            </div>

            {/* Miniatures */}
            {displayImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {displayImages.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setSelectedImage(index)}
                    className={`bg-white rounded-lg overflow-hidden border-2 ${
                      selectedImage === index ? 'border-primary-600' : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt || `${product.name} ${index + 1}`}
                      className="w-full h-24 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Informations produit */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>

            {/* Note et avis */}
            {product.average_rating && parseFloat(product.average_rating) > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <StarIconSolid
                      key={i}
                      className={`h-5 w-5 ${
                        i < Math.round(parseFloat(product.average_rating))
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-gray-600">
                  {product.average_rating} ({product.rating_count} avis)
                </span>
              </div>
            )}

            {/* Prix */}
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                {isOnSale ? (
                  <>
                    <span className="text-3xl font-bold text-primary-600">
                      {productsService.getFormattedPrice(currentPrice)}
                    </span>
                    <span className="text-xl text-gray-500 line-through">
                      {productsService.getFormattedPrice(regularPrice)}
                    </span>
                    {discountPercentage > 0 && (
                      <span className="bg-red-500 text-white px-2 py-1 rounded-md text-sm font-medium">
                        -{discountPercentage}%
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-3xl font-bold text-primary-600">
                    {productsService.getFormattedPrice(currentPrice)}
                  </span>
                )}
              </div>
            </div>

            {/* Description courte */}
            {product.short_description && (
              <div 
                className="prose prose-sm text-gray-600 mb-6"
                dangerouslySetInnerHTML={{ __html: product.short_description }}
              />
            )}

            {/* Variations */}
            {product.type === 'variable' && variationsData && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Options disponibles</h3>
                <div className="space-y-4">
                  {product.attributes.map((attribute) => (
                    <div key={attribute.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {attribute.name}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {attribute.options.map((option) => {
                          const matchingVariations = variationsData.variations.filter(v =>
                            v.attributes.some(a => a.name === attribute.name && a.option === option)
                          );
                          const isAvailable = matchingVariations.some(v => v.purchasable);
                          const isSelected = selectedVariation?.attributes.some(
                            a => a.name === attribute.name && a.option === option
                          );

                          return (
                            <button
                              key={option}
                              onClick={() => {
                                const variation = matchingVariations.find(v => v.purchasable);
                                if (variation) handleVariationChange(variation);
                              }}
                              disabled={!isAvailable}
                              className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                                isSelected
                                  ? 'border-primary-600 bg-primary-50 text-primary-700 font-medium'
                                  : isAvailable
                                  ? 'border-gray-300 hover:border-gray-400'
                                  : 'border-gray-200 text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Afficher les détails de la variation sélectionnée */}
                {selectedVariation && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {selectedVariation.attributes.map(a => a.option).join(' • ')}
                      </span>
                      {selectedVariation.sku && (
                        <span className="text-xs text-gray-500">SKU: {selectedVariation.sku}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stock */}
            <div className="mb-6">
              {isInStock ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckIcon className="h-5 w-5" />
                  <span>En stock</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-600">
                  <XMarkIcon className="h-5 w-5" />
                  <span>Rupture de stock</span>
                </div>
              )}
            </div>

            {/* Quantité et bouton d'ajout */}
            {isInStock && (
              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 hover:bg-gray-100"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 text-center border-x border-gray-300 py-2"
                    min="1"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-3 py-2 hover:bg-gray-100"
                  >
                    +
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={product.type === 'variable' && !selectedVariation}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  Ajouter au panier
                </button>
              </div>
            )}

            {/* Caractéristiques techniques */}
            {(getMetaValue(product, 'metal') || 
              getMetaValue(product, 'pierre') || 
              getMetaValue(product, 'poids_or') || 
              getMetaValue(product, 'dimension_du_motif') ||
              getMetaValue(product, 'dimension_du_bijou') ||
              getMetaValue(product, 'longueur_de_la_chaine') ||
              getMetaValue(product, 'delai_dexpedition')) && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">Caractéristiques</h3>
                <div className="space-y-3 text-sm">
                  {getMetaValue(product, 'metal') && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Métal :</span>
                      <span className="font-medium text-gray-900">{getMetaValue(product, 'metal')}</span>
                    </div>
                  )}
                  {getMetaValue(product, 'pierre') && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pierre :</span>
                      <span className="font-medium text-gray-900">{getMetaValue(product, 'pierre')}</span>
                    </div>
                  )}
                  {getMetaValue(product, 'poids_or') && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Poids :</span>
                      <span className="font-medium text-gray-900">{getMetaValue(product, 'poids_or')}</span>
                    </div>
                  )}
                  {(getMetaValue(product, 'dimension_du_motif') || getMetaValue(product, 'dimension_du_bijou')) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dimensions :</span>
                      <span className="font-medium text-gray-900">
                        {getMetaValue(product, 'dimension_du_motif') || getMetaValue(product, 'dimension_du_bijou')}
                      </span>
                    </div>
                  )}
                  {getMetaValue(product, 'longueur_de_la_chaine') && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Longueur de chaîne :</span>
                      <span className="font-medium text-gray-900">{getMetaValue(product, 'longueur_de_la_chaine')}</span>
                    </div>
                  )}
                  {getMetaValue(product, 'delai_dexpedition') && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Délai d'expédition :</span>
                      <span className="font-medium text-gray-900">{getMetaValue(product, 'delai_dexpedition')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Informations supplémentaires */}
            <div className="border-t pt-6 space-y-2 text-sm text-gray-600">
              {product.sku && (
                <p><span className="font-medium">SKU :</span> {product.sku}</p>
              )}
              {product.categories.length > 0 && (
                <p>
                  <span className="font-medium">Catégories :</span>{' '}
                  {product.categories.map(cat => cat.name).join(', ')}
                </p>
              )}
              {(product as any).brands && (product as any).brands.length > 0 && (
                <p>
                  <span className="font-medium">Marques :</span>{' '}
                  {(product as any).brands.map((brand: any) => brand.name).join(', ')}
                </p>
              )}
              {product.tags.length > 0 && (
                <p>
                  <span className="font-medium">Tags :</span>{' '}
                  {product.tags.map(tag => tag.name).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Description complète */}
        {product.description && (
          <div className="mt-12 bg-white rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Description</h2>
            <div 
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          </div>
        )}

        {/* Informations additionnelles du produit */}
        {getMetaValue(product, 'information') && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="text-sm text-blue-900">
              {getMetaValue(product, 'information')}
            </div>
          </div>
        )}

        {/* Produits recommandés (upsell) */}
        {upsellProducts && upsellProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Vous pourriez aussi aimer</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {upsellProducts.map((upsellProduct) => (
                <Link
                  key={upsellProduct.id}
                  to={`/products/${upsellProduct.slug}`}
                  className="group block"
                >
                  <div className="relative">
                    <div className="relative overflow-hidden bg-gray-50 aspect-square rounded">
                      {upsellProduct.images.length > 0 ? (
                        <img
                          src={upsellProduct.images[0].src}
                          alt={upsellProduct.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400">Pas d'image</span>
                        </div>
                      )}
                    </div>
                    <div className="pt-4">
                      <h3 className="text-gray-800 mb-2 font-light group-hover:text-gray-600 transition-colors line-clamp-2">
                        {upsellProduct.name}
                      </h3>
                      <div className="flex items-baseline justify-end gap-2">
                        {upsellProduct.on_sale ? (
                          <>
                            <span className="text-gray-500 line-through text-sm font-light">
                              {productsService.getFormattedPrice(upsellProduct.regular_price)}
                            </span>
                            <span className="text-gray-800 font-light">
                              {productsService.getFormattedPrice(upsellProduct.sale_price)}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-800 font-light">
                            {productsService.getFormattedPrice(upsellProduct.price)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Produits similaires */}
        {relatedProducts && relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Produits similaires</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {relatedProducts.slice(0, 4).map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/products/${relatedProduct.slug}`}
                  className="group block"
                >
                  <div className="relative">
                    <div className="relative overflow-hidden bg-gray-50 aspect-square rounded">
                      {relatedProduct.images.length > 0 ? (
                        <img
                          src={relatedProduct.images[0].src}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400">Pas d'image</span>
                        </div>
                      )}
                    </div>
                    <div className="pt-4">
                      <h3 className="text-gray-800 mb-2 font-light group-hover:text-gray-600 transition-colors line-clamp-2">
                        {relatedProduct.name}
                      </h3>
                      <div className="flex items-baseline justify-end gap-2">
                        {relatedProduct.on_sale ? (
                          <>
                            <span className="text-gray-500 line-through text-sm font-light">
                              {productsService.getFormattedPrice(relatedProduct.regular_price)}
                            </span>
                            <span className="text-gray-800 font-light">
                              {productsService.getFormattedPrice(relatedProduct.sale_price)}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-800 font-light">
                            {productsService.getFormattedPrice(relatedProduct.price)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
